import React, { FC, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Column, TableState } from 'react-table';
import Table from '../../components/Table';
import {
  Box,
  Container,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  makeStyles,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { SearchList, SearchResult } from '../../api/mobilbase/models';
import { SubmitHandler, useForm } from 'react-hook-form';
import LoadingButton from '../../components/buttons/LoadingButton';
import { useLocation } from 'react-router-dom';
import {
  RouteLocationProps,
  CheckoutLocationProps,
} from '../../routes/mobilbase';
import * as mobilbase from '../../api/mobilbase';
import NoDataDialog from '../../components/dialogs/ErrorDialog';
import FormDialog from '../../components/dialogs/FormDialog';
import APIRequest from '../../components/helpers/APIRequest';
import APIRequestPDF from '../../components/helpers/APIRequestPDF';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  grid: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  textHelp: {
    marginBottom: '1rem',
  },
}));

type FormValues = {
  tickerNumber: string;
  regNumber: string;
  cardNumber: string;
  days: string;
};

interface Props {
  navFunc: (historyState: CheckoutLocationProps) => void;
}

const LocationSearch: FC<Props> = ({ navFunc }) => {
  const classes = useStyles();
  const routeLocation = useLocation<RouteLocationProps>();
  const { selectedLocationId, selectedLocationName } = routeLocation.state
    ? routeLocation.state
    : new RouteLocationProps();
  const [data, setData] = useState<SearchList>({ result: [] });
  const [run, setRun] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    card: '',
    reg: '',
    ticket: '',
    days: 7,
  });
  const { register, handleSubmit, control } = useForm<FormValues>();

  const [receiptDataID, setReceiptDataID] = useState(0);
  const [receiptDataPayType, setReceiptDataPayType] = useState('');
  const [receiptDataBillet, setReceiptDataBillet] = useState('');
  const [receiptDataRegNrView, setReceiptDataRegNrView] = useState('');
  const [receiptDataKortNrView, setReceiptDataKortNrView] = useState('');
  const [receiptDataTime, setReceiptDataTime] = useState(new Date());
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [runGetReceipt, setRunGetReceipt] = useState(false);
  const [isRunGetReceiptPending, setRunGetReceiptPending] = useState(false);

  // Necessary for avoiding strikethrough on the location combobox
  const labelRef = useRef(null);
  // @ts-ignore: Object is possibly 'null'
  const labelWidth =
    labelRef !== null && labelRef.current !== null
      ? // @ts-ignore: Object is possibly 'null'
        labelRef.current.clientWidth
      : 0;

  useEffect(() => {
    if (!run && isPending) {
      if (data.result.length === 0) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  }, [run, isPending, data.result.length]);

  const columns: Column<SearchResult>[] = React.useMemo(
    () => [
      { Header: 'Automat navn', id: '0', accessor: 'automat' },
      { Header: 'BilletNr', id: '1', accessor: 'billet' },
      { Header: 'RegNr', id: '2', accessor: 'regNrView' },
      { Header: 'KortNr', id: '3', accessor: 'kortNrView' },
      {
        Header: 'Tid',
        id: '4',
        accessor: 'tid',
        Cell: ({ value }) => {
          return (
            moment(value).format('DD-MM-yyyy') +
            ' ' +
            moment(value).format('HH:mm')
          );
        },
      },
      { Header: 'Status', id: '5', accessor: 'status' },
      { Header: 'Handlinger', id: '6', accessor: 'action' },
    ],
    []
  );

  const initialState: Partial<TableState<SearchResult>> = {
    sortBy: [
      {
        id: 'tid',
        desc: true,
      },
    ],
  };

  const handleChange = (element: SearchResult) => {
    if (element.status.substr(0, 4) === 'inde') {
      navFunc(
        new CheckoutLocationProps(
          2,
          selectedLocationId,
          selectedLocationName,
          element.kortNrClear,
          element.kortNrView,
          element.regNrData,
          element.billet,
          element.tid
        )
      );
    } else {
      setReceiptDataID(element.id);
      setReceiptDataPayType(element.payType);
      setReceiptDataBillet(element.billet);
      setReceiptDataRegNrView(element.regNrView);
      setReceiptDataKortNrView(element.kortNrView);
      setReceiptDataTime(element.tid);
      setShowReceiptDialog(true);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    let days: number = data.days ? parseInt(data.days, 10) : 7;
    setFormData({
      card: data.cardNumber,
      reg: data.regNumber,
      ticket: data.tickerNumber,
      days: days,
    });
    setRun(true);
  };

  const RequestData = () => {
    return (
      <APIRequest<SearchList>
        run={run}
        setRun={setRun}
        pending={isPending}
        setIsPending={setIsPending}
        req={() =>
          mobilbase.searchLocation({
            locationId: selectedLocationId,
            body: {
              cardNbr: formData.card,
              regnr: formData.reg,
              ticket: formData.ticket,
              days: formData.days,
            },
          })
        }
        setData={setData}
      />
    );
  };

  const RequestReceipt = () => {
    return (
      <APIRequestPDF
        run={runGetReceipt}
        setRun={setRunGetReceipt}
        pending={isRunGetReceiptPending}
        setIsPending={setRunGetReceiptPending}
        req={() =>
          mobilbase.requestPDF(
            selectedLocationId,
            receiptDataID,
            receiptDataPayType
          )
        }
        fileName={`solvision-${receiptDataBillet}-${receiptDataRegNrView}.pdf`}
      />
    );
  };

  const handleCloseOffline = () => {
    setOpen(false);
  };

  return (
    <Container maxWidth={false}>
      <RequestData />
      <RequestReceipt />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5">
          Søgning -{' '}
          {selectedLocationName ? selectedLocationName : 'INGEN VALGT'}
        </Typography>
        <Container maxWidth="md">
          <div className={classes.paper}>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                type="number"
                id="tickerNumber"
                label="Billetnummer"
                name="tickerNumber"
                autoComplete="tickerNumber"
                autoFocus
                inputRef={register}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                name="regNumber"
                label="Registreringsnummer"
                id="regNumber"
                autoComplete="regNumber"
                inputRef={register}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                name="cardNumber"
                label="Kortnummer"
                id="cardNumber"
                autoComplete="cardNumber"
                inputRef={register}
              />
              <FormHelperText
                className={classes.textHelp}
                id="component-helper-text"
              >
                Søg på enten de 6 første cifre fx: 457123 eller hele nummeret
                fx: 457123XXXXXX1919
              </FormHelperText>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  ref={labelRef}
                  shrink
                  htmlFor="searchdays-input"
                  id="label-for-searchdays"
                >
                  Antal dage for søgning
                </InputLabel>
                <Controller
                  control={control}
                  name="days"
                  defaultValue="7"
                  as={
                    <Select
                      label="Antal dage for søgning"
                      labelWidth={labelWidth}
                      labelId="label-for-searchdays"
                      variant="outlined"
                      native
                      value={7}
                      id="searchdays-input"
                    >
                      <option value="7">7 dage</option>
                      <option value="30">30 dage</option>
                      <option value="90">90 dage</option>
                      <option value="180">180 dage</option>
                      <option value="300">300 dage</option>
                    </Select>
                  }
                />
              </FormControl>
              <Box mt={3}>
                <LoadingButton
                  update={() => {}}
                  pending={isPending}
                  text={'Søg'}
                />
              </Box>
            </form>
          </div>
        </Container>
        <div className={classes.grid}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Table<SearchResult>
                columns={columns}
                data={mapSearchData(data.result)}
                initialState={initialState}
                clickEvent={handleChange}
                update={null}
                pending={false}
                text={''}
              />
            </Grid>
          </Grid>
        </div>
      </div>

      <NoDataDialog
        open={open}
        onClose={handleCloseOffline}
        title={'Ingen resultater'}
        text={'Der kunne ikke findes nogle resultater for denne søgning'}
      />

      <FormDialog
        title={'PDF kvittering'}
        open={showReceiptDialog}
        onCloseCancel={() => setShowReceiptDialog(false)}
        onCloseSubmit={() => {
          setRunGetReceipt(true);
          setShowReceiptDialog(false);
        }}
      >
        <DialogContent>
          <p>Billet: {receiptDataBillet}</p>
          <p>Reg nr: {receiptDataRegNrView}</p>
          <p>Kort nr: {receiptDataKortNrView}</p>
          <p>
            Tid:{' '}
            {moment(receiptDataTime).format('DD-MM-yyyy') +
              ' ' +
              moment(receiptDataTime).format('HH:mm')}
          </p>
        </DialogContent>
      </FormDialog>
    </Container>
  );
};

const mapSearchData = (data: SearchResult[]) => {
  if (!data) {
    return [];
  }

  if (data.length === 0) {
    return [];
  }

  return data.map((d) => {
    var l = { ...d };
    l.billet = l.billet === '0' ? '-' : l.billet;
    if (l.status.substr(0, 4) === 'inde') {
      l.action = 'Vælg';
    } else if (l.status.trim() === 'ude') {
      l.action = '-';
    } else {
      l.action = 'Kvittering';
    }
    return l;
  });
};

export default LocationSearch;
